import { useEffect } from "react";

/**
 * Prevents and resets pinch-zoom in Capacitor native builds, and resets
 * accumulated zoom on orientation change everywhere.
 *
 * Native strategy:
 *  1. Intercept `touchmove` events with 2+ fingers and call preventDefault().
 *     This cancels the native WKWebView pinch-zoom gesture at the source —
 *     more reliable than `user-scalable=no` which iOS 16+ WKWebView ignores.
 *     NOTE: we do NOT set user-scalable=no permanently, because that also
 *     blocks zoom-OUT, leaving users stuck if zoom somehow still occurs.
 *  2. On orientation change, temporarily set maximum-scale=1 (after a short
 *     delay so the new layout has settled). iOS snaps the visual viewport back
 *     to scale=1 when it processes the new constraint. We restore the original
 *     meta after 400 ms so users can pinch-out if needed.
 *  3. visualViewport.resize fallback: if scale > 1 somehow slips through,
 *     trigger the snap reset.
 *
 * Web / PWA strategy:
 *  On orientationchange, temporarily clamp maximum-scale=1 then restore.
 */
export default function useResetZoomOnOrientationChange() {
  useEffect(() => {
    const viewport = document.querySelector<HTMLMetaElement>(
      'meta[name="viewport"]',
    );
    if (!viewport) return;

    const original = viewport.getAttribute("content") ?? "";
    const isNative =
      typeof (window as unknown as { Capacitor?: { isNative?: boolean } })
        .Capacitor !== "undefined";

    // Only add maximum-scale=1 — NOT user-scalable=no.
    // user-scalable=no prevents iOS from expanding the layout width in
    // landscape, causing the portrait width to be locked in.
    const withMaxScale1 = (base: string) =>
      base
        .replace(/,?\s*maximum-scale=[^,]*/gi, "")
        .trimEnd()
        .replace(/,$/, "") + ", maximum-scale=1";

    // Snaps the visual viewport back to scale=1 by briefly enforcing
    // maximum-scale=1, then restores the original meta so the user can
    // still pinch-out as an escape hatch.
    const snapZoom = () => {
      viewport.setAttribute("content", withMaxScale1(original));
      setTimeout(() => viewport.setAttribute("content", original), 400);
    };

    // Orientation change: only snap if already zoomed in.
    // Snapping at scale=1 fires a resize event during which visualViewport
    // scale may briefly hold a transient value, causing false ScreenTooSmall
    // triggers. If the user hasn't zoomed, there is nothing to reset.
    let orientationTimer: ReturnType<typeof setTimeout>;
    const handleOrientationChange = () => {
      clearTimeout(orientationTimer);
      if ((window.visualViewport?.scale ?? 1) > 1.01) {
        orientationTimer = setTimeout(snapZoom, 100);
      }
    };

    window.addEventListener("orientationchange", handleOrientationChange, {
      passive: true,
    });
    screen.orientation?.addEventListener("change", handleOrientationChange, {
      passive: true,
    } as AddEventListenerOptions);

    // Native only: block the pinch gesture at the touch level.
    const preventPinch = (e: TouchEvent) => {
      if (e.touches.length > 1) e.preventDefault();
    };

    // Native fallback: if scale > 1 slips through, snap it back.
    const vv = window.visualViewport;
    let zoomDebounce: ReturnType<typeof setTimeout>;
    const handleZoom = () => {
      if ((vv?.scale ?? 1) > 1.01) {
        clearTimeout(zoomDebounce);
        zoomDebounce = setTimeout(snapZoom, 50);
      }
    };

    if (isNative) {
      // passive: false is required to be able to call preventDefault()
      document.addEventListener("touchmove", preventPinch, { passive: false });
      if (vv) {
        vv.addEventListener("resize", handleZoom, { passive: true });
      }
    }

    return () => {
      clearTimeout(orientationTimer);
      clearTimeout(zoomDebounce);
      window.removeEventListener("orientationchange", handleOrientationChange);
      screen.orientation?.removeEventListener(
        "change",
        handleOrientationChange,
      );
      if (isNative) {
        document.removeEventListener("touchmove", preventPinch);
        vv?.removeEventListener("resize", handleZoom);
      }
    };
  }, []);
}
